// Short-term persistence for /generate results (Variant B feed).
//
// Backs the session feed with IndexedDB so a full page reload restores recent
// images/videos instead of starting fresh. IndexedDB (not localStorage) because
// it's async (no main-thread jank), has a large quota, and stores Blobs
// efficiently — putting large base64 into the synchronous ~5 MB localStorage is
// exactly what "fills up storage / freezes the browser".
//
// Images are stored as bytes (Blob); videos are stored as their remote URL only
// (no byte fetch) since a backend temp URL stays valid for the same 24 h window.
// Everything expires after 24 h to match the backend video timeout. Each pool has
// its own budget and LRU eviction. All ops fail soft: any storage error degrades
// to today's behaviour (feed lost on reload) and never breaks generation.

const DB_NAME = 'media-cache'
const DB_VERSION = 1
const IMAGES = 'images'
const VIDEOS = 'videos'

// Tunable. A single 24 h TTL governs both pools (matches the backend).
//
// These budgets are deliberately SMALL. On hydrate we convert every stored image
// blob back into a base64 string and hand them all to React state at once, so the
// disk budget becomes a resident-RAM budget on the next page load. Base64 is ~33%
// larger than the bytes, so an old 500 MB cache became ~650 MB of live JS strings
// (plus inline data-URIs in the DOM and decoded bitmaps) — enough to OOM-crash and
// reload mobile Safari/Chrome tabs. Keep the image byte budget within a mobile
// tab's headroom; older results still live in the feed until the session ends, they
// just aren't restored across a reload.
export const MEDIA_TTL_MS = 24 * 60 * 60 * 1000 // 24 h — images AND videos
const IMAGE_MAX_ITEMS = 24 // count backstop; RAM is bounded by the byte + hydrate caps
const IMAGE_MAX_BYTES = 30 * 1024 * 1024 // ~30 MB image bytes — safe on low-end PCs too
// Never convert more than this many stored images to base64 on a single reload,
// regardless of what survived on disk — the conversion itself allocates the base64.
// Kept small so a reload is cheap even on a weak machine; older results still live
// on disk under the TTL, they just aren't all rehydrated into memory at once.
const HYDRATE_MAX_ITEMS = 12
const VIDEO_MAX_ITEMS = 100 // videos are URL-only → count-bounded, ~0 bytes

const hasIDB = () => typeof indexedDB !== 'undefined'

let dbPromise = null

function openDB() {
  if (!hasIDB()) return Promise.resolve(null)
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    let req
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      resolve(null)
      return
    }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(IMAGES)) {
        db.createObjectStore(IMAGES, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(VIDEOS)) {
        db.createObjectStore(VIDEOS, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
    req.onblocked = () => resolve(null)
  }).catch(() => null)
  return dbPromise
}

// Promisify a single IDBRequest.
const reqToPromise = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })

async function getAll(store) {
  const db = await openDB()
  if (!db || !db.objectStoreNames.contains(store)) return []
  try {
    const tx = db.transaction(store, 'readonly')
    return (await reqToPromise(tx.objectStore(store).getAll())) || []
  } catch {
    return []
  }
}

async function putRecord(store, record) {
  const db = await openDB()
  if (!db) return
  try {
    const tx = db.transaction(store, 'readwrite')
    tx.objectStore(store).put(record)
    await new Promise((resolve) => {
      tx.oncomplete = resolve
      tx.onerror = resolve
      tx.onabort = resolve
    })
  } catch {
    /* fail soft */
  }
}

async function deleteIds(store, ids) {
  if (!ids || ids.length === 0) return
  const db = await openDB()
  if (!db || !db.objectStoreNames.contains(store)) return
  try {
    const tx = db.transaction(store, 'readwrite')
    const os = tx.objectStore(store)
    ids.forEach((id) => os.delete(id))
    await new Promise((resolve) => {
      tx.oncomplete = resolve
      tx.onerror = resolve
      tx.onabort = resolve
    })
  } catch {
    /* fail soft */
  }
}

// Raw base64 → a real Blob (structured-cloneable, so it survives in IndexedDB
// across reloads). NB: helpers' base64ToBlob returns an object-URL STRING, which
// is useless here — don't use it.
const base64ToBlob = async (base64, type = 'image/jpeg') => {
  const res = await fetch(`data:${type};base64,${base64}`)
  return res.blob()
}

// Convert a stored image Blob back to the raw base64 string the feed expects
// (FeedItem renders it as `data:image/jpeg;base64,${url}`).
const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })

// Drop oldest records until within both the item-count and byte budgets.
async function evict(store, maxItems, maxBytes) {
  const all = await getAll(store)
  if (all.length === 0) return
  all.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)) // oldest first
  const doomed = []
  let count = all.length
  let bytes = all.reduce((sum, r) => sum + (r.bytes || 0), 0)
  for (const r of all) {
    const overItems = count > maxItems
    const overBytes = maxBytes != null && bytes > maxBytes
    if (!overItems && !overBytes) break
    doomed.push(r.id)
    count -= 1
    bytes -= r.bytes || 0
  }
  await deleteIds(store, doomed)
}

/**
 * Persist one freshly generated image feed item. `item` is a feed item:
 * `{ id, url, isUrl, blurry, docRef, cloneData? }`. Fire-and-forget.
 */
export async function persistImage(uid, item) {
  if (!hasIDB() || !uid || !item?.id || item.url == null) return
  try {
    const now = Date.now()
    const record = {
      id: item.id,
      uid,
      type: 'image',
      isUrl: !!item.isUrl,
      blurry: !!item.blurry,
      docRef: item.docRef || null,
      // The settings behind the image, so Clone survives a reload. A few hundred bytes.
      cloneData: item.cloneData || null,
      url: null,
      blob: null,
      bytes: 0,
      createdAt: now,
      expiresAt: now + MEDIA_TTL_MS,
    }
    if (item.isUrl) {
      // Already hosted — store the URL string only.
      record.url = item.url
      record.bytes = item.url.length
    } else {
      // Raw base64 → real Blob so we don't bloat storage with a string.
      const blob = await base64ToBlob(item.url, 'image/jpeg')
      record.blob = blob
      record.bytes = blob.size
    }
    await putRecord(IMAGES, record)
    await evict(IMAGES, IMAGE_MAX_ITEMS, IMAGE_MAX_BYTES)
  } catch {
    /* fail soft */
  }
}

/**
 * Persist one freshly generated video feed item (URL only — no byte fetch).
 */
export async function persistVideo(uid, item) {
  if (!hasIDB() || !uid || !item?.id || !item.url) return
  try {
    const now = Date.now()
    const record = {
      id: item.id,
      uid,
      type: 'video',
      isUrl: true,
      blurry: !!item.blurry,
      docRef: item.docRef || null,
      url: item.url,
      // Which video model produced it, so a rehydrated feed still knows whether Extend
      // applies (v5/LTX has no extend pipeline). Null on records written before v5.
      videoVersion: item.videoVersion || null,
      // An extension's parent + choices, so its Redo survives a reload (plain JSON).
      redoOf: item.redoOf || null,
      // A story's scene: what it follows, and its picture's id or address (never base64) for its Try again.
      sceneOf: item.sceneOf || null,
      blob: null,
      bytes: item.url.length,
      createdAt: now,
      expiresAt: item.expiresAt || now + MEDIA_TTL_MS,
    }
    await putRecord(VIDEOS, record)
    await evict(VIDEOS, VIDEO_MAX_ITEMS, null)
  } catch {
    /* fail soft */
  }
}

/**
 * Rebuild the session feed from cache, newest first. Skips (and deletes) any
 * record past its TTL or belonging to another user. Returns feed items.
 */
export async function hydrateFeed(uid) {
  if (!hasIDB() || !uid) return []
  try {
    const now = Date.now()
    const [images, videos] = await Promise.all([getAll(IMAGES), getAll(VIDEOS)])

    const expiredImages = []
    const expiredVideos = []
    const live = []

    for (const r of images) {
      if (r.uid !== uid) continue
      if (!r.expiresAt || r.expiresAt <= now) {
        expiredImages.push(r.id)
        continue
      }
      live.push({ store: IMAGES, r })
    }
    for (const r of videos) {
      if (r.uid !== uid) continue
      if (!r.expiresAt || r.expiresAt <= now) {
        expiredVideos.push(r.id)
        continue
      }
      live.push({ store: VIDEOS, r })
    }

    await Promise.all([
      deleteIds(IMAGES, expiredImages),
      deleteIds(VIDEOS, expiredVideos),
    ])

    live.sort((a, b) => (b.r.createdAt || 0) - (a.r.createdAt || 0)) // newest first

    // Only rehydrate the newest N into memory. The rest stay on disk (pruned by TTL
    // / eviction) so we never allocate base64 for images the feed won't show anyway.
    const capped = live.slice(0, HYDRATE_MAX_ITEMS)

    const items = await Promise.all(
      capped.map(async ({ store, r }) => {
        try {
          if (store === VIDEOS) {
            return {
              id: r.id,
              type: 'video',
              url: r.url,
              docRef: r.docRef || undefined,
              isUrl: true,
              blurry: r.blurry,
              videoVersion: r.videoVersion || undefined,
              redoOf: r.redoOf || undefined,
              sceneOf: r.sceneOf, // null when none: src/lib/clip-chains.ts reads either
              saved: !!r.savedStatus,
              savedStatus: r.savedStatus || undefined,
              expiresAt: r.expiresAt,
            }
          }
          // image
          if (r.isUrl) {
            return {
              id: r.id,
              type: 'image',
              url: r.url,
              docRef: r.docRef || undefined,
              isUrl: true,
              blurry: r.blurry,
              cloneData: r.cloneData || undefined,
              saved: !!r.savedStatus,
              savedStatus: r.savedStatus || undefined,
              expiresAt: r.expiresAt,
            }
          }
          // Guard against legacy/corrupt records whose blob isn't a real Blob —
          // one bad record must not sink the whole restore.
          if (!(r.blob instanceof Blob)) return null
          const base64 = await blobToBase64(r.blob)
          return {
            id: r.id,
            type: 'image',
            url: base64,
            docRef: r.docRef || undefined,
            isUrl: false,
            blurry: r.blurry,
            cloneData: r.cloneData || undefined,
            saved: !!r.savedStatus,
            savedStatus: r.savedStatus || undefined,
            expiresAt: r.expiresAt,
          }
        } catch {
          return null
        }
      }),
    )
    return items.filter(Boolean)
  } catch {
    return []
  }
}

/**
 * Every cached result still within its TTL, as download-ready items, newest first.
 * Unlike hydrateFeed this is NOT capped and does NOT convert image bytes to base64 —
 * it hands back the stored Blob so the caller (Download All) can zip it directly,
 * with no base64 inflation. The cache only ever holds UNSAVED results (saving /
 * publishing / downloading removes them), so this is exactly "everything from the
 * last 24 h the user hasn't saved yet". Blurred/paywalled results are excluded.
 * Returns `{ id, type, isUrl, url, blob }[]`.
 */
export async function getDownloadableCached(uid) {
  if (!hasIDB() || !uid) return []
  try {
    const now = Date.now()
    const [images, videos] = await Promise.all([getAll(IMAGES), getAll(VIDEOS)])
    const pick = (records, type) =>
      records
        .filter(
          (r) =>
            r.uid === uid && r.expiresAt > now && !r.blurry && !r.savedStatus, // already saved/published → skip (don't re-download)
        )
        .map((r) => ({
          id: r.id,
          type,
          isUrl: !!r.isUrl,
          url: r.url || null,
          blob: r.blob instanceof Blob ? r.blob : null,
          createdAt: r.createdAt || 0,
        }))
    return [...pick(images, 'image'), ...pick(videos, 'video')]
      .sort((a, b) => b.createdAt - a.createdAt)
      .map(({ createdAt, ...item }) => item)
  } catch {
    return []
  }
}

/**
 * Remove one item from whichever pool holds it (delete only).
 */
export async function removeCached(uid, id) {
  if (!hasIDB() || !id) return
  await Promise.all([deleteIds(IMAGES, [id]), deleteIds(VIDEOS, [id])])
}

/**
 * Flag a cached record as saved/published IN PLACE instead of removing it, so it
 * stays in the feed (with its Saved/Published badge, across reloads) until the 24 h
 * TTL sweeps it — and Download All skips it. `status` is 'saved' or 'published';
 * published always wins, so an item that's both never downgrades to 'saved'.
 */
export async function markCachedSaved(uid, id, status) {
  if (!hasIDB() || !id) return
  const db = await openDB()
  if (!db) return
  // Issue the put synchronously inside the get's onsuccess so the transaction is
  // still active. Awaiting between get and put can let the tx auto-commit before
  // the put on some engines (notably older Safari — exactly our target), which
  // would silently drop the flag.
  const markInStore = (store) =>
    new Promise((resolve) => {
      let tx
      try {
        tx = db.transaction(store, 'readwrite')
      } catch {
        resolve()
        return
      }
      const os = tx.objectStore(store)
      const getReq = os.get(id)
      getReq.onsuccess = () => {
        const rec = getReq.result
        if (rec && (!uid || rec.uid === uid)) {
          rec.savedStatus =
            status === 'published' || rec.savedStatus === 'published'
              ? 'published'
              : 'saved'
          try {
            os.put(rec)
          } catch {
            /* fail soft */
          }
        }
      }
      getReq.onerror = () => {}
      tx.oncomplete = resolve
      tx.onerror = resolve
      tx.onabort = resolve
    })
  await Promise.all([markInStore(IMAGES), markInStore(VIDEOS)])
}

/**
 * Drop all past-TTL records. Returns the ids of expired videos so the live feed
 * can prune them too.
 */
export async function sweepExpired(uid) {
  if (!hasIDB()) return { expiredVideoIds: [] }
  try {
    const now = Date.now()
    const [images, videos] = await Promise.all([getAll(IMAGES), getAll(VIDEOS)])
    const expiredImages = images
      .filter((r) => !r.expiresAt || r.expiresAt <= now)
      .map((r) => r.id)
    const expiredVideoIds = videos
      .filter((r) => !r.expiresAt || r.expiresAt <= now)
      .map((r) => r.id)
    await Promise.all([
      deleteIds(IMAGES, expiredImages),
      deleteIds(VIDEOS, expiredVideoIds),
    ])
    return { expiredVideoIds }
  } catch {
    return { expiredVideoIds: [] }
  }
}

/**
 * Wipe the whole cache (sign-out / account switch / Reset). `uid` is accepted for
 * call-site symmetry; we clear everything so no account's media can leak.
 */
export async function clearCache() {
  const db = await openDB()
  if (!db) return
  try {
    const tx = db.transaction([IMAGES, VIDEOS], 'readwrite')
    tx.objectStore(IMAGES).clear()
    tx.objectStore(VIDEOS).clear()
    await new Promise((resolve) => {
      tx.oncomplete = resolve
      tx.onerror = resolve
      tx.onabort = resolve
    })
  } catch {
    /* fail soft */
  }
}
